// ============================================================================
// ARVEXIS PRESENCE — V4 fluid engine (Founder-approved, physically reviewed)
//
// The canonical launch asset IS the texture. Every pixel comes from
// /orb-canonical.webp: the engine scales it, resamples it through a
// noise-warped radial field, blooms it with its own colour and lifts its own
// centre into a core. The asset is never modified, recoloured or replaced.
//
// THREE states only — idle ("I am here"), listening ("I am hearing you"),
// radiating ("I am speaking to you"). There is no converging/processing state.
// Rotation is absolute 0.0 rev/s: the shader carries no rotation term at all,
// and the origin stays dead-centre (sampling is radial about the origin).
//
// Continuity (D1/D2): the noise phase and the breath phase are INTEGRATED, so
// a state change alters a rate and never a position — no jump, no reset flash.
// ============================================================================

export const PRESENCE_ASSET = '/orb-canonical.webp';

const VERT = `
attribute vec2 p; varying vec2 uv;
void main() { uv = p; gl_Position = vec4(p, 0.0, 1.0); }
`;

const FRAG = `
precision highp float;
varying vec2 uv;
uniform sampler2D uTex;      // the canonical launch asset
uniform float uPhase;        // integrated noise phase (never reset)
uniform float uDisp;         // surface displacement amplitude (fraction of radius)
uniform float uScale;        // global core scale
uniform float uBloom;        // bloom / glow emission
uniform float uBloomR;       // bloom radius factor (<1 compresses inward)
uniform float uCore;         // luminescent core weight
uniform float uAmp;          // real voice amplitude
uniform float uBreath;       // idle respiration, a narrow brightness arc

// --- Ashima 2D simplex noise ---
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x * 34.0) + 1.0) * x); }
float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz; x12.xy -= i1;
  i = mod289(i);
  vec3 pp = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m; m = m * m;
  vec3 x = 2.0 * fract(pp * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

// fluid lookup: the canonical texture sampled through a noise-warped radial field, so the asset's
// own fibers stretch, gather and ripple instead of the frame sliding around
vec2 warp(vec2 q) {
  float r = length(q) + 1e-5;
  vec2 dir = q / r;
  float n = snoise(dir * 1.15 + vec2(uPhase * 0.55, -uPhase * 0.42))
          + 0.45 * snoise(dir * 2.40 + vec2(-uPhase * 0.33, uPhase * 0.27));
  float shell = smoothstep(0.05, 0.85, r);          // interior stays calm, the shell breathes
  float rr = r * (1.0 - uDisp * n * 0.85 * shell);
  return dir * rr;
}

void main() {
  // no rotation term, no centre offset: the origin is locked dead-centre
  vec2 q = uv / max(uScale, 0.001);
  vec2 wq = warp(q);
  vec4 base = texture2D(uTex, wq * 0.5 + 0.5);
  if (length(wq) > 1.35) { gl_FragColor = vec4(0.0); return; }

  // bloom: the SAME asset, sampled on a widening spiral, added with its own colour — never white
  vec3 bloom = vec3(0.0);
  float wsum = 0.0;
  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    float ang = fi * 2.3999632;                     // golden-angle spiral
    float rad = (0.018 + 0.056 * fi / 7.0) * uBloomR;
    // the warp is evaluated ONCE per pixel and the taps offset from it
    vec2 bq = wq * (1.0 - rad * 0.55) + vec2(cos(ang), sin(ang)) * rad;
    vec4 s = texture2D(uTex, bq * 0.5 + 0.5);
    float w = 1.0 - fi / 9.0;
    bloom += s.rgb * s.a * w;
    wsum += w;
  }
  bloom /= max(wsum, 0.001);

  vec3 col = base.rgb + bloom * (0.85 * uBloom);

  // the dense luminescent core the quiet part of speech contracts into. Colour is taken from the
  // asset itself (a tight bloom of its own centre), so no new palette is introduced.
  float r = length(q);
  float coreMask = exp(-r * r * (5.0 + 26.0 * (1.0 - uBloomR)));
  vec3 coreCol = vec3(0.0);
  for (int k = 0; k < 4; k++) {
    float fk = float(k);
    float ang = fk * 2.3999632;
    vec2 off = vec2(cos(ang), sin(ang)) * (0.10 + 0.06 * fk / 3.0) * uBloomR;
    coreCol += texture2D(uTex, off * 0.5 + 0.5).rgb;
  }
  coreCol /= 4.0;
  col += coreCol * coreMask * (0.55 + 1.35 * uCore) * (0.35 + 0.65 * uBloom);

  // idle respiration: a narrow brightness arc; speaking amplitude rides on top
  col *= uBreath * (1.0 + 0.10 * uAmp);

  float alpha = clamp(base.a + length(bloom) * 0.55 * uBloom, 0.0, 1.0);
  col = col / (1.0 + col * 0.10);                   // gentle roll-off, keeps chroma
  // hue-preserving luminance ceiling: louder speech must not bleach the canonical palette
  float luma = dot(col, vec3(0.2126, 0.7152, 0.0722));
  if (luma > 0.62) col *= 0.62 / luma;
  gl_FragColor = vec4(col, alpha);
}
`;

// noiseHz = surface noise frequency · disp = displacement · scale = global core scale
// bloom = emission · radius = bloom radius factor · core = luminescent core weight
export const STATES = {
  idle:      { noiseHz: 0.5, disp: 0.050, scale: 1.00, bloom: 0.34, radius: 1.00, core: 0.20 },
  listening: { noiseHz: 3.0, disp: 0.150, scale: 0.90, bloom: 0.44, radius: 0.92, core: 0.26 },
  radiating: { noiseHz: 1.6, disp: 0.060, scale: 1.00, bloom: 0.50, radius: 0.70, core: 0.60 },
};

// the approved inverse core pump: silence contracts to the dense core, speech expands outward
export const PUMP = { scale: [0.86, 1.25], radius: [0.42, 1.01], core: [0.95, 0.33] };

const TAU = { state: 0.55, audio: 0.10 };
const ENV = { deadBand: 0.06, attack: 0.065, release: 0.30, macro: 1.6, headroom: 1.15 };
const BREATH_HZ = 0.5;      // "I am here" — the undulating idle breath
const BREATH_ARC = 0.05;
const MEMBER_CAP = 0.5;     // listening reads attention, not amplitude: a shout cannot flare it

// LONG-FORM RADIATING CONTINUITY.
// Talk reports the END of a turn when the provider has finished SENDING (response.done /
// response.audio.done), and it reports LISTENING the moment the mic opens — which, on a phone
// speaker, is often Arvexis's own voice echoing back mid-sentence. Both of those happen while the
// remote track is STILL AUDIBLY PLAYING, so presence used to leave the speaking behaviour long
// before she had finished speaking (measured: 20.6 s of a 52 s utterance, an 11.3 s continuous
// stretch). The real amplitude signal is valid for the whole utterance, so presence keeps
// representing her actual speech for as long as her real audio is actually audible:
//   above the floor  -> stay RADIATING and keep responding to the real amplitude
//   genuinely silent -> after SILENCE_RELEASE, hand the state back to whatever Talk asked for
// This is not a speaking timer and nothing is simulated: the ONLY thing that keeps the speaking
// behaviour alive is her own measured audio, and it contracts and releases when that audio stops.
const AUDIBLE_FLOOR = 0.012;   // measured silent-track floor is ~0.001-0.003 (p01 of a real read)
const SILENCE_RELEASE = 0.9;   // a pause between sentences reaches ~0.6-0.8 s in her real speech,
                               // so the release must outlast a pause or a breath reads as the end
                               // of the turn. While silent the pump is contracted anyway (amp 0).

const toward = (v, target, tau, dt) => v + (target - v) * (1 - Math.exp(-dt / tau));
const lerp = (a, b, k) => a + (b - a) * Math.min(Math.max(k, 0), 1);

let nextId = 1;

export const createPresenceV4 = (canvas, { maxPixels = 512 } = {}) => {
  const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false, antialias: false });
  if (!gl) return null;

  const compile = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(s);
      gl.deleteShader(s);
      throw new Error(log);
    }
    return s;
  };

  let prog;
  try {
    prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
  } catch {
    return null;
  }
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  const U = (n) => gl.getUniformLocation(prog, n);
  const u = {
    tex: U('uTex'), phase: U('uPhase'), disp: U('uDisp'), scale: U('uScale'),
    bloom: U('uBloom'), bloomR: U('uBloomR'), core: U('uCore'), amp: U('uAmp'), breath: U('uBreath'),
  };

  const tex = gl.createTexture();
  let ready = false;
  const img = new Image();
  img.onload = () => {
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.uniform1i(u.tex, 0);
    ready = true;
  };
  img.src = PRESENCE_ASSET;

  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const css = canvas.clientWidth || canvas.clientHeight || 1;
    const px = Math.max(1, Math.min(Math.round(css * dpr), maxPixels));
    if (canvas.width !== px || canvas.height !== px) {
      canvas.width = px;
      canvas.height = px;
    }
    gl.viewport(0, 0, canvas.width, canvas.height);
  };
  resize();

  const id = nextId++;
  const p = { ...STATES.idle };
  let requested = 'idle';       // what Talk (or the surface) asked for
  let state = 'idle';           // what presence shows, once audible continuity is applied
  let speakingTurn = false;     // her audio has started and has not yet gone genuinely silent
  let silentFor = 0;
  let arvexis = 0, member = 0;
  let phNoise = 0, phBreath = 0, env = 0, macro = 0, rms = 0;
  let last = performance.now();
  let raf = 0;
  let disposed = false;

  const telemetry = {
    id, state, requested, engine: 'v4-webgl', asset: PRESENCE_ASSET,
    rotation: 0, rotationVelocity: 0,
    scale: p.scale, disp: p.disp, bloom: p.bloom, radius: p.radius, core: p.core,
    amp: 0, arvexisVolume: 0, memberVolume: 0, breath: 1, textureReady: false, simulated: false,
    audible: false, heldByAudio: false, silentFor: 0,
  };
  if (typeof window !== 'undefined') {
    window.__arvexisPresence = window.__arvexisPresence || {};
    window.__arvexisPresence[id] = telemetry;
  }

  const frame = (now) => {
    const dt = Math.min((now - last) / 1000, 0.1);
    last = now;

    // ---- who is speaking, according to her real audio ----
    const audible = arvexis >= AUDIBLE_FLOOR;
    silentFor = audible ? 0 : silentFor + dt;
    if (requested === 'radiating') {
      speakingTurn = true;
      silentFor = audible ? 0 : silentFor;
    } else if (speakingTurn && silentFor >= SILENCE_RELEASE) {
      speakingTurn = false;     // the audio really has stopped: hand the state back to Talk
    }
    const heldByAudio = speakingTurn && requested !== 'radiating';
    state = speakingTurn ? 'radiating' : requested;

    // real signals only: Arvexis's own voice energy and the member's own mic energy
    rms = toward(rms, state === 'listening' ? member : 0, TAU.audio, dt);
    const raw = state === 'radiating'
      ? Math.max(0, (arvexis - ENV.deadBand) / (1 - ENV.deadBand)) : 0;
    env = toward(env, Math.min(raw, 1), raw > env ? ENV.attack : ENV.release, dt);
    macro = toward(macro, env, ENV.macro, dt);
    const amp = env ** ENV.headroom;

    const s = STATES[state];
    let tNoiseHz = s.noiseHz, tDisp = s.disp, tScale = s.scale,
        tBloom = s.bloom, tRadius = s.radius, tCore = s.core;
    if (state === 'listening') {
      const k = Math.min(rms / MEMBER_CAP, 1);
      tNoiseHz = 3.0 + 1.5 * k;
      tDisp = Math.min(0.15, 0.07 + 0.11 * k);
      tBloom = 0.40 + 0.14 * k;
    } else if (state === 'radiating') {
      // the heartbeat: quiet contracts toward the concentrated core, speech expands outward
      tScale = lerp(PUMP.scale[0], PUMP.scale[1], amp);
      tRadius = lerp(PUMP.radius[0], PUMP.radius[1], amp);
      tCore = lerp(PUMP.core[0], PUMP.core[1], amp);
      tDisp = 0.055 + 0.085 * amp;
      tBloom = 0.42 + 0.34 * amp;
    }
    const tau = state === 'radiating' ? TAU.audio : TAU.state;
    p.noiseHz = toward(p.noiseHz, tNoiseHz, TAU.state, dt);
    p.disp = toward(p.disp, tDisp, tau, dt);
    p.scale = toward(p.scale, tScale, tau, dt);
    p.bloom = toward(p.bloom, tBloom, tau, dt);
    p.radius = toward(p.radius, tRadius, tau, dt);
    p.core = toward(p.core, tCore, tau, dt);

    // integrated phases: a state change alters a rate, never a position
    phNoise += dt * p.noiseHz;
    phBreath += dt * BREATH_HZ;
    const breath = 1 + BREATH_ARC * Math.sin(phBreath * Math.PI * 2);

    if (ready) {
      gl.uniform1f(u.phase, phNoise);
      gl.uniform1f(u.disp, p.disp);
      gl.uniform1f(u.scale, p.scale);
      gl.uniform1f(u.bloom, p.bloom);
      gl.uniform1f(u.bloomR, p.radius);
      gl.uniform1f(u.core, p.core);
      gl.uniform1f(u.amp, amp);
      gl.uniform1f(u.breath, breath);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    telemetry.state = state;
    telemetry.requested = requested;
    telemetry.audible = audible;
    telemetry.heldByAudio = heldByAudio;
    telemetry.silentFor = +silentFor.toFixed(3);
    telemetry.scale = +p.scale.toFixed(4);
    telemetry.disp = +p.disp.toFixed(4);
    telemetry.bloom = +p.bloom.toFixed(3);
    telemetry.radius = +p.radius.toFixed(3);
    telemetry.core = +p.core.toFixed(3);
    telemetry.amp = +amp.toFixed(4);
    telemetry.arvexisVolume = +arvexis.toFixed(4);
    telemetry.memberVolume = +member.toFixed(4);
    telemetry.breath = +breath.toFixed(4);
    telemetry.macro = +macro.toFixed(4);
    telemetry.noiseHz = +p.noiseHz.toFixed(3);
    telemetry.phase = +phNoise.toFixed(3);
    telemetry.textureReady = ready;

    if (!disposed) raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  return {
    setState: (next) => { if (STATES[next]) requested = next; },
    setSignals: (a, m) => { arvexis = a || 0; member = m || 0; },
    resize,
    dispose: () => {
      disposed = true;
      cancelAnimationFrame(raf);
      gl.deleteBuffer(buf);
      gl.deleteTexture(tex);
      gl.deleteProgram(prog);
      if (typeof window !== 'undefined' && window.__arvexisPresence) {
        delete window.__arvexisPresence[id];
      }
      // the context is deliberately NOT force-lost: the same canvas element can be re-initialised
      // (React re-runs mount effects in development) and a lost context never compiles again.
    },
  };
};
